import { create } from "zustand";
import { IProfile, ISetReminder, IUpdateProfile } from "../@types";
import { profileService } from "../services";
import i18n from "../i18n";
import {
  BUSINESS_TYPE_RETAIL,
  STORAGE_USER_DATA,
  BUDGET,
  BANK_ACCOUNTS,
  REMINDER,
  NOTIFICATION_SETTINGS,
} from "../constants";
import { convertImageToBase64 } from "../utils/convertToBase64";
import { BankData, Budget, BusinessType } from "../types";
import { createBankAccount, updateProfile } from "../backend-services";
import { CognitoUser, CognitoUserSession } from "amazon-cognito-identity-js";

type NotificationSettings = NonNullable<IProfile["notificationSettings"]>;

interface CompanyState {
  isLoading: boolean;
  company: IProfile;
  session: CognitoUserSession | null;
  cognitoUser: CognitoUser | null;
  setCognitoUser: (user: CognitoUser | null) => void;
  setSession: (session: CognitoUserSession | null) => void;
  setCompany: (company: any) => void;
  setShowTodo: (value: boolean) => void;
  budget: Budget | undefined;
  showTodo: boolean;
  listInvoice: boolean;
  listInventory: boolean;
  firstLogin: boolean;
  setBudget: (budget: Budget) => void;
  companyBankAccounts: BankData[];
  setCompanyBankAccounts: (accounts: BankData[]) => void;
  reminder: ISetReminder | null;
  setReminder: (reminder: ISetReminder | null) => void;
  hasReminder: boolean;
  fetchCompanyProfile: () => Promise<IProfile>;
  isRetailBusiness: () => boolean;
  locale: string;
  setLocale: (locale: string) => void;
  brandColor: string;
  updateBusinessType: (type: BusinessType) => Promise<void>;
  updateCompanyProfile: (profileData: IUpdateProfile) => Promise<void>;
  createBankAccount: (data: BankData) => Promise<BankData[]>;
  updateNotificationSettings: (settings: NotificationSettings) => Promise<void>;
  updateProfile: (profileData: Partial<IUpdateProfile>) => void;
  setFirstLogin: (status: boolean) => void;
  setListInvoice: (status: boolean) => void;
  setListInventory: (status: boolean) => void;
}

export const useCompanyStore = create<CompanyState>((set, get) => {
  const parseJSON = (item: string | null | undefined, defaultValue: any) => {
    try {
      return item !== null && item !== undefined
        ? JSON.parse(item)
        : defaultValue;
    } catch (e) {
      console.error("Error parsing JSON:", e);
      return defaultValue;
    }
  };

  const budget = parseJSON(localStorage.getItem(BUDGET) ?? null, {});
  const companyBankAccounts = parseJSON(
    localStorage.getItem(BANK_ACCOUNTS) ?? null,
    [],
  );
  const reminder = parseJSON(
    localStorage.getItem(REMINDER) ?? null,
    null,
  ) as ISetReminder | null;
  const notificationSettings = parseJSON(
    localStorage.getItem(NOTIFICATION_SETTINGS),
    {
      restockAlert: { stock: 5, channels: ["email"], enabled: false },
      marketing: { enabled: false },
      weeklyReport: { email: [] },
    },
  );
  const getDefaultNotificationSettings = () => ({
    restockAlert: { stock: 5, channels: ["email"], enabled: false },
    marketing: { enabled: false },
    weeklyReport: { email: [] },
  });

  return {
    company: {} as IProfile,
    isLoading: false,
    budget,
    session: null,
    cognitoUser: null,
    companyBankAccounts,
    notificationSettings,
    reminder,
    hasReminder: reminder !== null,
    locale: localStorage.getItem("locale") || "en",
    brandColor: "#000000",
    showTodo: true,
    listInvoice: true,
    listInventory: true,
    firstLogin: true,

    setCompany: (company: IProfile & { logoBase64?: string }) => {
      const hasLogoData = company.logoBase64 !== undefined;
      const updatedUser = { ...get().company, ...company };
      if (hasLogoData) updatedUser.logoBase64 = company.logoBase64;
      localStorage.setItem(STORAGE_USER_DATA, JSON.stringify(updatedUser));
      set({ company: updatedUser });
    },

    setBudget: (budget: Budget) => {
      const currentBudget = JSON.stringify(budget);
      localStorage.setItem(BUDGET, currentBudget);
      set({ budget });
    },
    setCognitoUser: (user: CognitoUser | null) => {
      set({ cognitoUser: user });
    },
    setSession: (session: CognitoUserSession | null) => {
      set({ session: session });
    },
    setShowTodo: (value: boolean) => {
      set({ showTodo: value });
    },

    setCompanyBankAccounts: (accounts) => {
      localStorage.setItem(BANK_ACCOUNTS, JSON.stringify(accounts));
      set({ companyBankAccounts: accounts });
    },

    setReminder: (reminder: ISetReminder | null) => {
      if (reminder) {
        localStorage.setItem(REMINDER, JSON.stringify(reminder));
        set({ reminder, hasReminder: true });
      } else {
        localStorage.removeItem(REMINDER);
        set({ reminder: null, hasReminder: false });
      }
    },
    setListInvoice: (status: boolean) => {
      set({ listInvoice: status });
    },
    setListInventory: (status: boolean) => {
      set({ listInventory: status });
    },
    updateProfile: async (profileData: Partial<IUpdateProfile>) => {
      try {
        await updateProfile(profileData);
        const updatedCompany = { ...get().company, ...profileData };
        set({ company: updatedCompany });
      } catch (err) {
        console.error("Error updating business type:", err);
        throw err;
      }
    },
    setFirstLogin: (status: boolean) => {
      const updatedUser = { ...get().company, firstLogin: status };
      localStorage.setItem(STORAGE_USER_DATA, JSON.stringify(updatedUser));
      set({ company: updatedUser, firstLogin: status });
    },
    fetchCompanyProfile: async () => {
      set({ isLoading: true });
      try {
        const res = await profileService.get(`/companies/profile`);
        const companyProfile = res.data;

        if (companyProfile.logoUrl) {
          convertImageToBase64(
            companyProfile.logoUrl,
            (base64) =>
              set({ company: { ...companyProfile, logoBase64: base64 } }),
            () => set({ company: companyProfile }),
          );
        } else {
          set({ company: companyProfile, isLoading: false });
        }
        return companyProfile;
      } catch (e: any) {
        set({ isLoading: false });
        console.error("Error fetching company profile:", e);
      }
    },

    isRetailBusiness: () => {
      const { company } = get();
      return company?.businessType === BUSINESS_TYPE_RETAIL;
    },

    setLocale: (locale) => {
      i18n.changeLanguage(locale);
      localStorage.setItem("locale", locale);
      set({ locale });
    },

    updateBusinessType: async (type: BusinessType) => {
      try {
        await updateProfile({ businessType: type });
        const updatedUser = { ...get().company, businessType: type };
        set({ company: updatedUser });
      } catch (err) {
        console.error("Error updating business type:", err);
        throw err;
      }
    },

    updateCompanyProfile: async (profileData: Partial<IUpdateProfile>) => {
      try {
        await updateProfile(profileData);
        const updatedUser = { ...get().company, ...profileData };
        set({ company: updatedUser });
      } catch (err) {
        console.error("Error updating business type:", err);
        throw err;
      }
    },

    createBankAccount: async (data: BankData): Promise<BankData[]> => {
      try {
        const result = await createBankAccount(data);
        const currentUser = get().company;
        const updatedUser = {
          ...currentUser,
          bankAccounts: [...(currentUser.bankAccounts || []), ...result.data],
        };
        set({ company: updatedUser });
        return result.data;
      } catch (err) {
        console.error("Error creating bank account:", err);
        throw err;
      }
    },

    updateNotificationSettings: async (settings: NotificationSettings) => {
      try {
        const currentCompany = get().company;

        // Prepare payload exactly as backend expects
        const updatePayload = {
          restockAlert: {
            stock: settings.restockAlert?.stock ?? 5,
            channels: settings.restockAlert?.channels ?? ["email"],
            enabled: settings.restockAlert?.enabled ?? false,
          },
          marketing: {
            enabled: settings.marketing?.enabled ?? false,
          },
          weeklyReport: {
            email: settings.weeklyReport?.email ?? [],
          },
        };

        // Make POST request to the correct endpoint
        await profileService.post(`/companies/set-notification`, updatePayload);

        // Update local state
        const updatedCompany = {
          ...currentCompany,
          notificationSettings: updatePayload,
        };

        set({ company: updatedCompany });
        localStorage.setItem(STORAGE_USER_DATA, JSON.stringify(updatedCompany));
      } catch (err: any) {
        console.error(
          "Error updating notification settings:",
          err?.response?.data || err.message,
        );
        const fallbackCompany = JSON.parse(
          localStorage.getItem(STORAGE_USER_DATA) || "{}",
        );
        set({ company: fallbackCompany });
        throw err;
      }
    },
  };
});
